import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMutation, useQueryClient } from 'react-query';
import {
  fetchBuildingDetails,
  fetchInactiveResidentCount,
  updateBuilding,
  updateBuildingImage,
  updateDashboardConfig
} from '~/api/building';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import ImageDropzone from '~/components/ImageDropzone/ImageDropzone';
import Spinner from '~/components/Spinner/Spinner';
import { qrCodeGeneratorURL } from '~/helpers/constants';
import useIsMounted from '~/hooks/isMounted';
// @ts-ignore
import Switch from 'react-ios-switch';

import {
  BuildingDetails,
  UpdateBuildingDashboardConfig
} from '~/types/building';
import classes from './EditBuildingContainer.module.css';
import { RootState } from '~/redux/store';
import { connect } from 'react-redux';
import { Roles } from '~/enums/Roles';
import { useBuildingDashboardConfig } from '~/hooks/useBuildingDashboardConfig';
import GenericModal from '~/components/GenericModal/GenericModal';
import { EditBuildingSetting } from './EditBuildingSettings/EditBuildingSetting';
import Spacer from '~/components/Spacer/Spacer';
import { EditReviewSetting } from './EditBuildingSettings/EditReviewSetting';
import { EditBuildingKeys } from './EditBuildingKeys';
import useBuildingMobileConfig from '~/hooks/building/useBuildingMobileConfig';
import { buildingQueryKey } from '~/hooks/building/useBuilding';
import { VENDOR_NAME_KEY } from '~/theme/vendorConstants';
import HomebodyActivationChecksModal from '~/components/HomebodyActivationChecksModal/HomebodyActivationChecksModal';

type IProps = ReturnType<typeof mapStateToProps>;

const EditBuildingContainer = (props: IProps) => {
  const { userRole } = props;
  const { buildingId } = useParams<{ buildingId: string }>();
  const [buildingDetails, setBuildingDetails] = useState({} as BuildingDetails);
  const [image, setImage] = useState({} as File);
  const [loading, setLoading] = useState(true);
  const [showImageSpinner, setShowImageSpinner] = useState(false);
  const [snippetsText, setSnippetsText] = useState(false);
  const [showSwitchToPartnerModal, setShowSwitchToPartnerModal] = useState(false);
  const [showSwitchToCobuModal, setShowSwitchToCobuModal] = useState(false);
  const [showInactivityCronModal, setShowInactivityCronModal] = useState(false);
  const [showActivationChecks, setShowActivationChecks] = useState(false);
  // null while the count is loading, -1 if the lookup failed.
  const [inactiveCount, setInactiveCount] = useState<number | null>(null);
  const history = useHistory();

  const {
    hidePerks,
    showGoogleReview,
    residentsCanCreateEvents,
    showDM,
    showGroups,
    hideEvents,
    updateBuildingConfigs,
    loadingMobileSettings
  } = useBuildingMobileConfig(buildingId);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const showEditSetting = queryParams.get('showEditSetting') || false;

  const isVendorMode = sessionStorage.getItem(VENDOR_NAME_KEY) !== null;
  const showEditBuildingSetting =
    userRole === Roles.Vcm || userRole === Roles.Superadmin;

  const isMounted = useIsMounted();

  const getValidCode = (): any => {
    return buildingDetails.codes.filter((c) => c.valid)[0];
  };

  const getValidBranchCode = (): any => {
    return buildingDetails.deeplink;
  };

  const queryClient = useQueryClient();
  const { data } = useBuildingDashboardConfig(buildingId);

  const { mutate: updateBDC, isLoading } = useMutation(
    async (bdc: UpdateBuildingDashboardConfig) => {
      return updateDashboardConfig(buildingId, bdc);
    },
    {
      onSuccess: async (buildingDashboadConfigUpdated) => {
        queryClient.setQueryData(
          ['buildingDashboardConfig', buildingId],
          buildingDashboadConfigUpdated
        );

        const csvUrl = buildingDashboadConfigUpdated.data?.csvExportUrl;
        if (csvUrl) {
          toast(
            <span>
              Event source switched. Previous events exported and cancelled.{' '}
              <a href={csvUrl} target='_blank' rel='noopener noreferrer'>Download CSV</a>
            </span>,
            { autoClose: false }
          );
        } else {
          toast('Building config has been successfully changed!');
        }
      },
      onError: (err: Error) => {
        toast(err?.message || 'Something went wrong! Please try again later.');
      }
    }
  );

  const fetchBuilding = (): Promise<void> => {
    return fetchBuildingDetails(buildingId).then((res) => {
      if (isMounted()) {
        setBuildingDetails(res.data);
        setImage(res.data.image);
        setLoading(false);
        setSnippetsText(res.data.showCommentSnippets);
      }
    });
  };

  useEffect(() => {
    fetchBuilding();
  }, [buildingId, isMounted]);

  const onEditBuildingEmail = () => {
    history.push(`/building/${buildingId}/edit/emails`);
  };

  const onEditBuildingAddress = () => {
    history.push(`/building/${buildingId}/edit/address`);
  };

  const onEditBuildingImages = () => {
    history.push(`/building/${buildingId}/edit/images`);
  };

  const hasBuildingData = !!Object.keys(buildingDetails).length;

  return (
    <div className={`content-container`}>
      <Breadcrumbs />
      <div className={classes.headingContainer}>
        <h1 className='h1-title'>Building Settings</h1>
        {showEditBuildingSetting && hasBuildingData && (
          <div className={classes.headingContainer}>
            <EditBuildingKeys
              buildingId={buildingId}
              onEditBuilding={fetchBuilding}
              showEditSetting={showEditSetting as boolean}
            />
            <Spacer width={20} />
            <button
              className='btn-standard btn-primary'
              onClick={onEditBuildingAddress}
            >
              Edit Building Address
            </button>
            <Spacer width={20} />
            <button
              className='btn-standard btn-primary'
              onClick={onEditBuildingImages}
            >
              Edit Building Images
            </button>
            <Spacer width={20} />
            {(userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
              <>
                <button
                  className='btn-standard btn-primary'
                  onClick={onEditBuildingEmail}
                >
                  Edit Building Emails
                </button>
                <Spacer width={20} />
              </>
            )}
            {buildingDetails.homebody?.isIntegrated &&
              (userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
                <>
                  <button
                    className='btn-standard btn-primary'
                    onClick={() => setShowActivationChecks(true)}
                  >
                    Run Homebody Checks
                  </button>
                  <Spacer width={20} />
                  <HomebodyActivationChecksModal
                    buildingId={buildingId}
                    showModal={showActivationChecks}
                    onChange={setShowActivationChecks}
                    onCompleted={fetchBuilding}
                  />
                </>
              )}
            <EditBuildingSetting
              buildingId={buildingId}
              building={buildingDetails}
              onEditBuilding={fetchBuilding}
              showEditSetting={showEditSetting as boolean}
            />
            <Spacer width={20} />
            <EditReviewSetting
              building={buildingDetails}
              onEditBuilding={fetchBuilding}
            />
          </div>
        )}
      </div>
      {loading && <Spinner />}
      {!loading && (
        <div className={`${classes.container}`}>
          <div>
            <p className='small-text bold'>BUILDING</p>
            <Spacer height={8} />
            {!isVendorMode && (
              <>
                <p className='small-text '>Building Code</p>
                <Spacer height={8} />
              </>
            )}
            <div className='display-flex'>
              {!isVendorMode && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginRight: '64px',
                    gap: '16px'
                  }}
                >
                  {(!buildingDetails.homebody?.isIntegrated ||
                    buildingDetails.homebody?.cobuOwnedEvents ||
                    !(buildingDetails.homebody?.qrCodeUrl ||
                      buildingDetails.homebody?.appUrl ||
                      buildingDetails.homebody?.appName)) &&
                    getValidBranchCode() && (
                      <div
                        className={`${classes.content} ${classes.newQRCode} margin-right-32`}
                      >
                        <p className='font-14 light-grey-text margin-top-8'>
                          QR code
                        </p>
                        <Spacer height={8} />
                        <div className={classes.codeWrap}>
                          <div>
                            <img
                              className={classes.qrCode}
                              src={qrCodeGeneratorURL(getValidBranchCode())}
                              alt='New QR Code'
                            />
                          </div>
                          <div className={classes.CodeDetail}>
                            <p className='small-text bold'>
                              <strong>
                                NEW QR Code. USE ON ALL MARKETING MATERIAL.
                              </strong>
                            </p>
                            <p className='small-text'>
                              Building Code:{' '}
                              <strong>{getValidCode().code}</strong>
                            </p>
                            <p className='small-text'>
                              Magic link:&nbsp;
                              <strong>
                                <a href={getValidBranchCode()}>
                                  {getValidBranchCode()}
                                </a>
                              </strong>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  {buildingDetails.homebody?.isIntegrated &&
                  (buildingDetails.homebody?.qrCodeUrl ||
                    buildingDetails.homebody?.appUrl ||
                    buildingDetails.homebody?.appName) && (
                    // Homebody whitelabel building: show the partner app QR / universal URL
                    <>
                      {buildingDetails.homebody?.cobuOwnedEvents && (
                        <p className='small-text bold'>
                          Homebody Access Information
                        </p>
                      )}
                      <div
                        className={`${classes.content} ${classes.newQRCode} margin-right-32`}
                      >
                        <p className='font-14 light-grey-text margin-top-8'>
                          App QR code
                        </p>
                        <Spacer height={8} />
                        <div className={classes.codeWrap}>
                          {buildingDetails.homebody?.qrCodeUrl && (
                            <div>
                              <img
                                className={classes.qrCode}
                                src={buildingDetails.homebody?.qrCodeUrl}
                                alt='App QR Code'
                              />
                            </div>
                          )}
                          <div className={classes.CodeDetail}>
                            <p className='small-text bold'>
                              <strong>
                                {buildingDetails.homebody?.appName || 'App'}
                              </strong>
                            </p>
                            {buildingDetails.homebody?.appUrl && (
                              <p className='small-text'>
                                Universal URL:&nbsp;
                                <strong>
                                  <a
                                    href={buildingDetails.homebody?.appUrl}
                                    target='_blank'
                                    rel='noreferrer'
                                  >
                                    {buildingDetails.homebody?.appUrl}
                                  </a>
                                </strong>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {(userRole === Roles.Pm || userRole === Roles.PM_FREE || userRole === Roles.Vcm || userRole === Roles.Superadmin) &&
                    buildingDetails.homebody?.loginUrl &&
                    /^https?:\/\//i.test(buildingDetails.homebody.loginUrl) && (
                    <p className='small-text'>
                      Login URL:&nbsp;
                      <strong>
                        <a
                          href={buildingDetails.homebody.loginUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          {buildingDetails.homebody.loginUrl}
                        </a>
                      </strong>
                    </p>
                  )}
                  {/* Original QR Code Section - Visible to VCM & Superadmin */}
                  {/*
                  <div
                    className={`${classes.content} ${classes.originalQRCode} margin-right-32`}
                  >
                    <p className='font-14 light-grey-text margin-top-8'>
                      QR code
                    </p>
                    <Spacer height={8} />
                    <div className={classes.codeWrap}>
                      <div>
                        <img
                          className={classes.qrCode}
                          src={qrCodeGeneratorURL(getValidCode().link)}
                          alt='Original QR Code'
                        />
                      </div>
                      <div className={classes.CodeDetail}>
                        <p className='small-text bold'>
                          <strong>
                            Original QR Code. Only use for historical purposes.
                          </strong>
                        </p>
                        <p className='small-text'>
                          Building Code: <strong>{getValidCode().code}</strong>
                        </p>
                        <p className='small-text'>
                          Magic link:&nbsp;
                          <strong>
                            <a href={getValidCode().link}>
                              {getValidCode().link}
                            </a>
                          </strong>
                        </p>
                      </div>
                    </div>
                  </div>
                  */}
                </div>
              )}
              <div>
                <p className='font-14 light-grey-text'>Add a Photo</p>
                <ImageDropzone
                  image={image}
                  spinner={showImageSpinner}
                  handleDrop={async (acceptedFiles: File[]) => {
                    setShowImageSpinner(true);
                    updateBuildingImage(
                      { image: acceptedFiles[0] },
                      buildingId
                    ).then(() => {
                      queryClient.invalidateQueries(buildingQueryKey(buildingId));
                      toast('Image succesfully updated.');
                      setImage(acceptedFiles[0]);
                    });
                    setShowImageSpinner(false);
                  }}
                />
              </div>
            </div>
          </div>
          {(userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
            <div>
              <p className='small-text bold'>PLAN</p>
              <div className={`display-flex`}>
                <div className={classes.toggleContainer}>
                  <Switch
                    checked={data?.data.config.isCobuPlus}
                    onChange={(currentSwitchState: boolean) => {
                      updateBDC({
                        isCobuPlus: currentSwitchState
                      });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Cobu Plus</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {(userRole === Roles.Vcm || userRole === Roles.Superadmin) &&
            data?.data.config.hasOwnProperty('isPaid') && (
              <div>
                <p className='small-text bold'>PARTNER PLAN</p>
                <div className={`display-flex`}>
                  <div className={classes.toggleContainer}>
                    <Switch
                      checked={data?.data.config.isPaid}
                      onChange={(currentSwitchState: boolean) => {
                        updateBDC({
                          isPaid: currentSwitchState
                        });
                      }}
                      disabled={isLoading}
                    />
                    <div className='margin-left-16'>
                      <p>Premium</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          {(userRole === Roles.Vcm || userRole === Roles.Superadmin) &&
            data?.data.config.isPaid === true && (
              <div>
                <p className='small-text bold'>EVENT SOURCE</p>
                <div className='display-flex'>
                  <button
                    className={`${classes.sourcePickerBtn} ${classes.sourcePickerBtnLeft} ${data?.data.config.eventSource !== 'cobu' ? classes.sourcePickerBtnSelected : ''}`}
                    onClick={() => data?.data.config.eventSource === 'cobu' && setShowSwitchToPartnerModal(true)}
                    disabled={isLoading}
                  >
                    Partner
                  </button>
                  <button
                    className={`${classes.sourcePickerBtn} ${classes.sourcePickerBtnRight} ${data?.data.config.eventSource === 'cobu' ? classes.sourcePickerBtnSelected : ''}`}
                    onClick={() => data?.data.config.eventSource !== 'cobu' && setShowSwitchToCobuModal(true)}
                    disabled={isLoading}
                  >
                    Cobu
                  </button>
                </div>
                <Spacer height={8} />
                {data?.data.config.eventSource === 'cobu' ? (
                  <p className='small-text light-grey-text'>
                    Events are managed in the Cobu dashboard and not synced with the Partner.
                    Switching to Partner will export all Cobu events to CSV and cancel them.
                  </p>
                ) : (
                  <p className='small-text light-grey-text'>
                    Events are synced with the Partner. Admins cannot create events via the dashboard.
                    Switching to Cobu will export all Partner events to CSV and cancel them.
                  </p>
                )}
                {data?.data.config.lastEventSourceSwitchCsvUrl && (
                  <>
                    <Spacer height={8} />
                    <a
                      href={data.data.config.lastEventSourceSwitchCsvUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='small-text'
                    >
                      Download last event source switch export
                    </a>
                  </>
                )}
                <GenericModal
                  showModal={showSwitchToPartnerModal}
                  title='Switch to Partner Events'
                  content={
                    <p>
                      Switching to Partner mode will export all Cobu events and RSVPs to a CSV
                      file and then cancel them. Residents will no longer see these events.
                      A download link will appear after the switch completes.
                    </p>
                  }
                  actionText='Confirm switch'
                  actionFunction={() => {
                    updateBDC({ eventSource: 'partner' });
                    setShowSwitchToPartnerModal(false);
                  }}
                  onChange={setShowSwitchToPartnerModal}
                />
                <GenericModal
                  showModal={showSwitchToCobuModal}
                  title='Switch to Cobu Events'
                  content={
                    <p>
                      Switching to Cobu mode will export all Partner (Entrata) events and RSVPs
                      to a CSV file and then cancel them. Residents will no longer see those
                      events. A download link will appear after the switch completes.
                    </p>
                  }
                  actionText='Confirm switch'
                  actionFunction={() => {
                    updateBDC({ eventSource: 'cobu' });
                    setShowSwitchToCobuModal(false);
                  }}
                  onChange={setShowSwitchToCobuModal}
                />
              </div>
            )}
          {(userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
            <div>
              <p className='small-text bold'>COMMENTS</p>
              <div className={`display-flex`}>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={snippetsText}
                    onChange={() => {
                      const currentSnippetsText = snippetsText;
                      setSnippetsText((prevState) => !prevState);
                      updateBuilding(
                        {
                          showCommentSnippets: !currentSnippetsText
                        },
                        buildingId
                      )
                        .then(() => {
                          toast('Snippets succesfully updated.');
                        })
                        .catch((e) => {
                          toast(
                            'Something went wrong! Please try again later.'
                          );
                          setSnippetsText(currentSnippetsText);
                        });
                    }}
                  />
                  <div className='margin-left-16'>
                    <p>Show comment snippets in push notifications</p>
                  </div>
                </div>
                <div className={classes.toggleContainer}>
                  <Switch
                    checked={!data?.data.config.showDeletedCommentsToPM}
                    onChange={(currentSwitchState: boolean) => {
                      if (currentSwitchState) {
                        updateBDC({
                          showDeletedCommentsToPM: !currentSwitchState,
                          showSentimentDataToPM: false
                        });
                      } else {
                        updateBDC({
                          showDeletedCommentsToPM: !currentSwitchState
                        });
                      }
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Hide hidden and deleted comments for PM role</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {(userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
            <div>
              <p className='small-text bold'>RESIDENTS</p>
              <div className={`display-flex`}>
                {/* PM blocking is not offered on Cobu buildings. */}
                {buildingDetails.homebody?.isIntegrated && (
                  <div className={`${classes.toggleContainer} margin-right-24`}>
                    <Switch
                      checked={data?.data.config.allowPmBlocking ?? true}
                      onChange={(currentSwitchState: boolean) => {
                        updateBDC({
                          allowPmBlocking: currentSwitchState
                        });
                      }}
                      disabled={isLoading}
                    />
                    <div className='margin-left-16'>
                      <p>Allow PM role to block residents</p>
                    </div>
                  </div>
                )}
                <div className={classes.toggleContainer}>
                  <Switch
                    checked={data?.data.config.inactivityCronEnabled ?? false}
                    onChange={(currentSwitchState: boolean) => {
                      // Turning off is not destructive, so it applies straight away.
                      if (!currentSwitchState) {
                        updateBDC({ inactivityCronEnabled: false });
                        return;
                      }
                      setInactiveCount(null);
                      setShowInactivityCronModal(true);
                      fetchInactiveResidentCount(buildingId)
                        .then((res) => setInactiveCount(res.data.count))
                        .catch(() => setInactiveCount(-1));
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Auto-deactivate residents inactive for 180+ days</p>
                  </div>
                </div>
              </div>
              <GenericModal
                showModal={showInactivityCronModal}
                title='Turn on auto-deactivation'
                content={
                  <ul>
                    <li>
                      Residents who have not opened the app in 180 days will be
                      deactivated.
                    </li>
                    {buildingDetails.hasPmsIntegration && (
                      <li>
                        PMS sync is enabled for this building and already deactivates
                        residents who move out.
                      </li>
                    )}
                    <li>
                      {inactiveCount === null && 'Checking how many residents…'}
                      {inactiveCount === -1 && 'Could not check how many residents.'}
                      {inactiveCount !== null && inactiveCount >= 0 && (
                        <strong>
                          {inactiveCount === 1
                            ? '1 resident will be deactivated tonight.'
                            : `${inactiveCount} residents will be deactivated tonight.`}
                        </strong>
                      )}
                    </li>
                  </ul>
                }
                actionText='Turn on'
                actionFunction={() => {
                  updateBDC({ inactivityCronEnabled: true });
                  setShowInactivityCronModal(false);
                }}
                onChange={setShowInactivityCronModal}
              />
            </div>
          )}
          {(userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
            <div>
              <p className='small-text bold'>ONBOARDING</p>
              <div className={`display-flex`}>
                <div className={classes.toggleContainer}>
                  <Switch
                    checked={data?.data.config.onboardingPostsEnabled}
                    onChange={(currentSwitchState: boolean) => {
                      updateBDC({
                        onboardingPostsEnabled: currentSwitchState
                      });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Post Morgan's first-week onboarding messages to the feed</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {(userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
            <div>
              <p className='small-text bold'>REVIEWS</p>
              <div className={`display-flex`}>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={data?.data.config.showGoogleReviewLinkInApp}
                    onChange={(currentSwitchState: boolean) => {
                      updateBDC({
                        showGoogleReviewLinkInApp: currentSwitchState
                      });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Show Google Review Link in the Me Section</p>
                  </div>
                </div>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={data?.data.config.showReviewsToPM}
                    onChange={(currentSwitchState: boolean) => {
                      updateBDC({
                        showReviewsToPM: currentSwitchState
                      });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Display reviews to PM roles</p>
                  </div>
                </div>
                <div className={classes.toggleContainer}>
                  <Switch
                    checked={data?.data.config.autoSendReviews}
                    onChange={(currentSwitchState: boolean) => {
                      updateBDC({
                        autoSendReviews: currentSwitchState
                      });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Auto invite Residents to leave Google reviews</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {(userRole === Roles.Vcm || userRole === Roles.Superadmin) &&
            data?.data.config.showDeletedCommentsToPM && (
              <div>
                <p className='small-text bold'>SENTIMENT</p>
                <div className={`display-flex`}>
                  <div className={`${classes.toggleContainer} margin-right-24`}>
                    <Switch
                      checked={data?.data.config.showSentimentDataToPM}
                      onChange={(currentSwitchState: boolean) => {
                        updateBDC({
                          showSentimentDataToPM: currentSwitchState
                        });
                      }}
                      disabled={isLoading}
                    />
                    <div className='margin-left-16'>
                      <p>Display sentiment data to PM role</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          {(userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
            <div>
              <p className='small-text bold'>GATHERINGS</p>
              <div className={`display-flex margin-bottom-24`}>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={data?.data.config.showGatheringTemplates}
                    onChange={(currentSwitchState: boolean) => {
                      updateBDC({
                        showGatheringTemplates: currentSwitchState,
                        gatheringTemplateLevel: true
                      });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Allow PM role to see gathering templates</p>
                  </div>
                </div>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={data?.data.config.showGatheringEventReminder}
                    onChange={(currentSwitchState: boolean) => {
                      updateBDC({
                        showGatheringEventReminder: currentSwitchState
                      });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Allow gathering reminders</p>
                  </div>
                </div>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={data?.data.config.allowFlyerRequest}
                    onChange={(currentSwitchState: boolean) => {
                      updateBDC({
                        allowFlyerRequest: currentSwitchState
                      });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Allow gathering flyer request</p>
                  </div>
                </div>
                {data?.data.config.allowFlyerRequest && (
                  <div className={`${classes.toggleContainer} margin-right-24`}>
                    <Switch
                      checked={data?.data.config.showFlyerToggleToPm}
                      onChange={(currentSwitchState: boolean) => {
                        updateBDC({
                          showFlyerToggleToPm: currentSwitchState
                        });
                      }}
                      disabled={isLoading}
                    />
                    <div className='margin-left-16'>
                      <p>Show flyer toggle to PM</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {(userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
            <div>
              <p className='small-text bold'>APP SETTINGS</p>
              <div className={`display-flex margin-bottom-32`}>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={!hidePerks}
                    onChange={(currentSwitchState: boolean) => {
                      updateBuildingConfigs({
                        hidePerks: !currentSwitchState,
                        showGoogleReview,
                        residentsCanCreateEvents,
                        showDM,
                        showGroups,
                        hideEvents
                      });
                    }}
                    disabled={loadingMobileSettings}
                  />
                  <div className='margin-left-16'>
                    <p>Show Local in Mobile App</p>
                  </div>
                </div>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={showGoogleReview}
                    onChange={(currentSwitchState: boolean) => {
                      updateBuildingConfigs({
                        hidePerks,
                        showGoogleReview: currentSwitchState,
                        residentsCanCreateEvents,
                        showDM,
                        showGroups,
                        hideEvents
                      });
                    }}
                    disabled={loadingMobileSettings}
                  />
                  <div className='margin-left-16'>
                    <p>Show Google Review Prompt in Gathering Feedback</p>
                  </div>
                </div>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={residentsCanCreateEvents}
                    onChange={(currentSwitchState: boolean) => {
                      updateBuildingConfigs({
                        hidePerks,
                        showGoogleReview,
                        residentsCanCreateEvents: currentSwitchState,
                        showDM,
                        showGroups,
                        hideEvents
                      });
                    }}
                    disabled={loadingMobileSettings}
                  />
                  <div className='margin-left-16'>
                    <p>Residents Can Create Events</p>
                  </div>
                </div>
              </div>
              <div className={`display-flex margin-bottom-32`}>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={showDM}
                    onChange={(currentSwitchState: boolean) => {
                      updateBuildingConfigs({
                        hidePerks,
                        showGoogleReview,
                        residentsCanCreateEvents,
                        showDM: currentSwitchState,
                        showGroups,
                        hideEvents
                      });
                    }}
                    disabled={loadingMobileSettings}
                  />
                  <div className='margin-left-16'>
                    <p>Residents Can DM</p>
                  </div>
                </div>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={showGroups}
                    onChange={(currentSwitchState: boolean) => {
                      updateBuildingConfigs({
                        hidePerks,
                        showGoogleReview,
                        residentsCanCreateEvents,
                        showDM,
                        showGroups: currentSwitchState,
                        hideEvents
                      });
                    }}
                    disabled={loadingMobileSettings}
                  />
                  <div className='margin-left-16'>
                    <p>Residents Can Join Groups</p>
                  </div>
                </div>
                {data?.data.config.hasOwnProperty('isPaid') && (
                  <div className={`${classes.toggleContainer} margin-right-24`}>
                    <Switch
                      checked={hideEvents}
                      onChange={(currentSwitchState: boolean) => {
                        updateBuildingConfigs({
                          hidePerks,
                          showGoogleReview,
                          residentsCanCreateEvents,
                          showDM,
                          showGroups,
                          hideEvents: currentSwitchState
                        });
                      }}
                      disabled={loadingMobileSettings}
                    />
                    <div className='margin-left-16'>
                      <p>Hide Events Tab</p>
                    </div>
                  </div>
                )}
                {(userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
                  <div className={`${classes.toggleContainer} margin-right-24`}>
                    <Switch
                      checked={data?.data.config.showPlaces}
                      onChange={(currentSwitchState: boolean) => {
                        updateBDC({
                          showPlaces: currentSwitchState
                        });
                      }}
                      disabled={isLoading}
                    />
                    <div className='margin-left-16'>
                      <p>Enable Places</p>
                    </div>
                  </div>
                )}
                {(userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
                  <div className={`${classes.toggleContainer} margin-right-24`}>
                    <Switch
                      checked={data?.data.config.showMarketplaceV2}
                      onChange={(currentSwitchState: boolean) => {
                        updateBDC({
                          showMarketplaceV2: currentSwitchState
                        });
                      }}
                      disabled={isLoading}
                    />
                    <div className='margin-left-16'>
                      <p>Enable Marketplace listings</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {(userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
            <div>
              <p className='small-text bold'>COMMUNITY</p>
              <div className={`display-flex margin-bottom-32`}>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={!!data?.data.config.pauseCommunity}
                    onChange={(currentSwitchState: boolean) => {
                      const message = currentSwitchState
                        ? 'Pause this community? Residents will not be able to post, comment, react, send direct messages or create events, and no push notifications will be sent. They can still open the app and read. Nothing is deleted.'
                        : 'Resume this community? Residents will be able to post, comment and send direct messages again.';
                      if (!window.confirm(message)) return;
                      updateBDC({
                        pauseCommunity: currentSwitchState
                      });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Pause community</p>
                    <p className='small-text'>
                      Residents can read but not post, comment, react or send DMs; no push
                      notifications. Building admins can still post. Fully reversible.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {(userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
            <div>
              <p className='small-text bold'>PUSH NOTIFICATIONS</p>
              <div className={`display-flex margin-bottom-32`}>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={!!data?.data.config.pausePushNotifications}
                    onChange={(currentSwitchState: boolean) => {
                      updateBDC({
                        pausePushNotifications: currentSwitchState
                      });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Pause all push notifications</p>
                    <p className='small-text'>
                      No pushes to anyone in this building (posts, comments, DMs, events,
                      reminders). The community keeps working; emails have their own switches.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {(userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
            <div>
              <p className='small-text bold'>EMAILS</p>
              <div className={`display-flex margin-bottom-32`}>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={data?.data.config.enableBuildingReport}
                    onChange={(currentSwitchState: boolean) => {
                      updateBDC({
                        enableBuildingReport: currentSwitchState
                      });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Send B2B Monthly Report Emails</p>
                  </div>
                </div>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={data?.data.config.enableBuildingDigestEmail}
                    onChange={(currentSwitchState: boolean) => {
                      updateBDC({
                        enableBuildingDigestEmail: currentSwitchState
                      });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Send B2C Digest Emails</p>
                  </div>
                </div>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={
                      data?.data.config?.hasOwnProperty('enableNoEventReminder')
                        ? data?.data.config.enableNoEventReminder
                        : true
                    }
                    onChange={(currentSwitchState: boolean) => {
                      updateBDC({
                        enableNoEventReminder: currentSwitchState
                      });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Enable No Event Reminder - Monthly (2nd Tuesday)</p>
                  </div>
                </div>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={!data?.data.config.disableFeedbackNotificationEmail}
                    onChange={(currentSwitchState: boolean) => {
                      updateBDC({ disableFeedbackNotificationEmail: !currentSwitchState });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Email on-site staff on post-event resident feedback</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {(userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
            <div>
              <p className='small-text bold'>PERKS</p>
              <div className={`display-flex margin-bottom-32`}>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={data?.data.config.showPerks}
                    onChange={(currentSwitchState: boolean) => {
                      updateBDC({
                        showPerks: currentSwitchState
                      });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Enable Perks for the building</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {userRole === Roles.Superadmin && (
            <div>
              <p className='small-text bold'>UGC</p>
              <div className={`display-flex margin-bottom-32`}>
                <div className={`${classes.toggleContainer} margin-right-24`}>
                  <Switch
                    checked={data?.data.config.showUGC}
                    onChange={(currentSwitchState: boolean) => {
                      updateBDC({
                        showUGC: currentSwitchState
                      });
                    }}
                    disabled={isLoading}
                  />
                  <div className='margin-left-16'>
                    <p>Enable User Generated Content</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function mapStateToProps(state: RootState) {
  return {
    userRole: state.auth.userRole
  };
}

export default connect(mapStateToProps)(EditBuildingContainer);
